<template>
  <!-- 考核统计：按销售目标达成情况计算综合得分与评级 -->
  <report-layout active="assess">
    <list-page>
      <search-condition @search="loadReport" :show-search="true" page-name="assessReport">
        <template #default>
          <el-select v-model="query.year" placeholder="年度" @change="loadReport">
            <template #prefix>年度</template>
            <el-option v-for="y in yearOptions" :key="y" :label="y + ' 年'" :value="y"/>
          </el-select>
          <el-select v-model="query.businessType" placeholder="业务类型" @change="loadReport">
            <template #prefix>业务类型</template>
            <el-option v-for="b in businessTypes" :key="b.value" :label="b.label" :value="b.value"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">考核人数</div>
              <div class="value">{{ rows.length }}<span class="unit">人</span></div>
              <div class="sub">已配置年度销售目标的人员</div>
            </div>
            <div class="stat-card">
              <div class="label">目标合计</div>
              <div class="value money">{{ money(totalTarget) }}<span class="unit">{{ unit }}</span></div>
            </div>
            <div class="stat-card">
              <div class="label">已达成合计</div>
              <div class="value money success">{{ money(totalAchieved) }}<span class="unit">{{ unit }}</span></div>
            </div>
            <div class="stat-card">
              <div class="label">整体达成率</div>
              <div class="value">{{ overallRate }}<span class="unit">%</span></div>
            </div>
          </div>

          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">考核得分排行</div>
              <div class="rank-list">
                <div v-for="(row, index) in rankRows" :key="row.employeeId" class="rank-item">
                  <span class="rank-no" :class="index < 3 ? 't' + (index + 1) : ''">{{ index + 1 }}</span>
                  <span class="rank-name">{{ row.employeeName }}</span>
                  <span class="rank-sub">{{ row.recordCount || 0 }} 单</span>
                  <span class="rank-val">{{ row.score }} 分</span>
                </div>
                <div v-if="!rankRows.length" class="muted" style="padding: 12px 4px">暂无数据</div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">考核得分与达成率</div>
              <div ref="scoreChart" class="chart-div"></div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">考核明细</div>
            <el-table :data="rows" border size="small" empty-text="暂无数据" max-height="460">
              <el-table-column label="销售人员" prop="employeeName" min-width="140">
                <template v-slot="{row}"><span class="name-link">{{ row.employeeName }}</span></template>
              </el-table-column>
              <el-table-column label="业绩条数" prop="recordCount" align="right" width="110">
                <template v-slot="{row}"><span class="num">{{ row.recordCount || 0 }}</span></template>
              </el-table-column>
              <el-table-column label="目标" prop="target" align="right" width="150">
                <template v-slot="{row}"><span class="num">{{ money(row.target) }}</span></template>
              </el-table-column>
              <el-table-column label="已达成" prop="achieved" align="right" width="150">
                <template v-slot="{row}"><span class="num bold">{{ money(row.achieved) }}</span></template>
              </el-table-column>
              <el-table-column label="目标达成率" prop="rate" min-width="170">
                <template v-slot="{row}">
                  <div class="ratio-cell">
                    <span class="ratio-track">
                      <i :style="{width: Math.min(100, Number(row.rate || 0)) + '%', background: rateColor(row.rate)}"></i>
                    </span>
                    <span class="ratio-text">{{ row.rate || 0 }}%</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="综合得分" prop="score" align="right" width="110">
                <template v-slot="{row}"><span class="num bold">{{ row.score }}</span></template>
              </el-table-column>
              <el-table-column label="评级" prop="grade" align="center" width="120">
                <template v-slot="{row}">
                  <el-tag size="small" :type="row.gradeType">{{ row.grade }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="状态" prop="status" align="center" width="100">
                <template v-slot="{row}">
                  <span :class="{muted: !row.status}">{{ row.status || '—' }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import saleTargetApi from "@/api/dz-crm/sale/saleTargetApi";
import {barOption, money} from "@/views/h5/crm/report/reportUtil";

const BUSINESS_TYPES = [
  {value: 1, label: "毛利"},
  {value: 2, label: "新客户"},
  {value: 3, label: "客户转介绍"},
  {value: 4, label: "服务费"},
];

export default {
  name: "AssessReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton},
  data() {
    const year = new Date().getFullYear();
    return {
      loading: false,
      businessTypes: BUSINESS_TYPES,
      yearOptions: [year, year - 1, year - 2, year - 3],
      query: {year, businessType: 1},
      perf: {},
      scoreChart: null,
    };
  },
  computed: {
    unit() {
      return this.perf.unit || "元";
    },
    rows() {
      return (this.perf.rows || []).map(row => {
        const rate = Number(row.rate || 0);
        const recordCount = Number(row.recordCount || 0);
        const score = Math.round(rate * 0.6 + recordCount * 1.2);
        let grade = "C · 合格";
        let gradeType = "warning";
        if (score >= 110) {
          grade = "A · 优秀";
          gradeType = "success";
        } else if (score >= 80) {
          grade = "B · 良好";
          gradeType = "primary";
        }
        return {...row, rate, recordCount, score, grade, gradeType};
      });
    },
    rankRows() {
      return this.rows.slice().sort((a, b) => b.score - a.score).slice(0, 10);
    },
    totalTarget() {
      return this.rows.reduce((sum, row) => sum + Number(row.target || 0), 0);
    },
    totalAchieved() {
      return this.rows.reduce((sum, row) => sum + Number(row.achieved || 0), 0);
    },
    overallRate() {
      if (!this.totalTarget) {
        return 0;
      }
      return Math.round(this.totalAchieved / this.totalTarget * 10000) / 100;
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    this.loadReport();
  },
  unmounted() {
    if (this.scoreChart) {
      this.scoreChart.dispose();
    }
  },
  methods: {
    money,
    async loadReport() {
      this.loading = true;
      try {
        this.perf = await saleTargetApi.performance({
          year: this.query.year,
          businessType: this.query.businessType,
        }) || {};
        this.$nextTick(() => this.renderChart());
      } finally {
        this.loading = false;
      }
    },
    renderChart() {
      const dom = this.$refs.scoreChart;
      if (!dom) {
        return;
      }
      if (!this.scoreChart) {
        this.scoreChart = markRaw(echarts.init(dom));
      }
      const rows = this.rows;
      this.scoreChart.setOption(barOption(
          rows.map(row => row.employeeName),
          rows.map(row => row.score),
          "#7a5af8",
      ), true);
    },
    rateColor(rate) {
      const n = Number(rate || 0);
      if (n >= 100) {
        return "#2e8b57";
      }
      if (n >= 80) {
        return "#1677ff";
      }
      return "#e6a23c";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

.name-link {
  color: #303133;
  font-weight: 500;
}
</style>
